<template>
  <div>
    <div v-if="isFetching" class="page-loader">
      <c-loader />
    </div>

    <div v-else>
      <div class="upgrade-ticket-wrapper">
        <!-- Mobile Header with Pay Button -->
        <div :class="{'sticky-mobile-header': isMobile}" v-if="isMobile">
          <div class="payment-block no-shadow-mobile">
            <div>
              <p style="font-size: 15px;">
                <strong><i class="fa-regular fa-circle-info text-info"></i>
                Forgot to add something during registration?</strong>
              </p>
              <p style="font-size: 15px;">
                No problem. Simply select your desired add-on, enter your card information and click "Pay".
              </p>
              <p v-if="addonsHistory?.length" style="font-size: 15px;">
                <a
                  href="#"
                  @click.prevent="viewPurchasedAddons"
                  style="font-size: 15px; color: #337ab7; text-decoration: underline;"
                >View Purchased Add-Ons</a>
              </p>

              <div class="mobile-only-pay" >
                <c-button
                  v-if="showPaymentForm && !successMsg"
                  theme="primary"
                  size="sm"
                  icon="fa-credit-card"
                  :disabled="loading || successMsg !== ''"
                  :show-indicator="showIndicator || loading"
                  fill
                  @click="onMobilePayClick"
                  style="width: 100%;"
                >{{ payButtonLabel }}</c-button>
              </div>
            </div>
          </div>
        </div>

        <h3 v-if="!isMobile">Upgrade Ticket</h3>
        <div v-if="!isMobile" style="padding: 20px 0;">
          <h4>
            <i class="fa-regular fa-circle-info text-info"></i>
            Forgot to add something during registration?
          </h4>
          <p style="margin: 5px 0; font-size: 16px;">
            No problem. Simply select your desired add-on, enter your card information and click "Pay".
          </p>
          <p v-if="addonsHistory?.length" style="margin: 0;">
            <a
              href="#"
              @click.prevent="viewPurchasedAddons"
              style="font-size: 16px; color: #337ab7; text-decoration: underline;"
            >View Purchased Add-Ons</a>
          </p>
        </div>

        <div class="payment-layout">
          <!-- Left Block: Addons Selection -->
          <div class="payment-block" :class="{'full-width-block': isMobile}">
            <validation-observer v-slot="{ invalid, valid, errors }" ref="form">
              <form @submit.prevent="">
                <template v-for="(addon, addonsIndex) in addons" >
                  <div class="addon-wrapper item-row" :key="`ticket-addons-${addonsIndex}`">
                    <div class="addon-info-wrapper">
                      <h5 class="addon-title">
                        {{ addon.title }}
                        <span v-if="addon.limit > 0"> - {{ addon.actual_limit }} Spaces Left</span>
                      </h5>
                      <div class="addon-price default-color">
                        ${{ addon.price }}
                      </div>
                      <div class="addon-description">
                        <ul class="addon_sessions" v-if="addon.sessions?.length">
                          <li
                            v-if="addon.sessions.length === 1"
                            class="addon_sessions__item"
                          >
                            <router-link :to="{ name: 'Upgrade Ticket Session Detail', params: { sessionId: addon.sessions[0].id },  query: {back: 'Upgrade Ticket'} }">View More Details</router-link>
                          </li>
                          <li
                            v-else
                            class="addon_sessions__item"
                            @click="openSessionsModal(addon)"
                          >
                            View More Details
                          </li>
                        </ul>
                        <p v-if="!(addon.has_session && isMobile)">
                          {{ addon.description }}
                        </p>
                      </div>
                    </div>
                    <div class="addon-quantity-wrapper">
                      <select class="form-control addons" v-model="addon.selected" @change="onUpdateSelectedAddons">
                        <option value="0">0</option>
                        <option
                          v-for="quantity in addon.available_limit"
                          :key="`addon-${addon.id}-quantity-${quantity}`"
                          :value="quantity">
                          {{ quantity }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <hr v-if="(addonsIndex + 1) !== addons.length" />
                </template>
              </form>
            </validation-observer>
          </div>

          <!-- Right Block: Stripe Form (Desktop Only) -->
          <div class="payment-block" v-if="!isMobile">
            <div v-if="showPaymentForm && !successMsg && isInitializingStripe" class="text-center p-5">
              <c-loader />
              <p class="mt-2 text-muted">Loading payment form...</p>
            </div>
            <div v-if="successMsg">
              <h3 class="text-center">Payment Success</h3>
              <br /><br />
              <h3 class="text-center text-success">
                <i class="fa-regular fa-circle-check text-success"></i> {{successMsg}}
              </h3>
            </div>

            <div v-if="showPaymentForm && !successMsg" v-show="!isInitializingStripe">
              <div class="card" v-if="paymentInfo.payment_required">
                <div class="card-header payment-method-header">
                  <div class="payment-method payment-method-logos stripe-wrapper">
                    <!-- Visa -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 24" role="img" width="38" height="24"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z" fill="#142688"/></svg>
                    <!-- Mastercard -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 24" role="img" width="38" height="24"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><circle fill="#EB001B" cx="15" cy="12" r="7"/><circle fill="#F79E1B" cx="23" cy="12" r="7"/><path fill="#FF5F00" d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"/></svg>
                    <small class="payment-method-read-more">and more...</small>
                  </div>
                </div>
                <div class="card-body p-3 payment-method-block" id="card-stripe-form">
                  <div v-show="expressAvailable">
                    <div id="express-checkout-element"></div>
                    <div class="express-divider"><span>or pay with card</span></div>
                  </div>
                  <div id="card-element"></div>
                  <div v-if="errorMessage" class="text-center text-danger mt-2">{{ errorMessage }}</div>
                </div>
              </div>

              <div v-else class="free-addon-notice text-center p-4">
                <i class="fa-regular fa-circle-check text-success" style="font-size: 24px;"></i>
                <p class="mt-2 mb-0">This add-on is free. Click below to add it to your ticket.</p>
                <div v-if="errorMessage" class="text-center text-danger mt-2">{{ errorMessage }}</div>
              </div>

              <div class="purchase-button-wrapper">
                <c-button
                  theme="primary"
                  size="md"
                  icon="fa-arrow-right"
                  :show-indicator="loading"
                  fill
                  uppercase
                  @click="onHandleMakePayment()"
                >{{ payButtonLabel }}</c-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stripe Modal (Mobile Only) -->
      <c-modal
        v-if="isMobile && isPaymentModalOpen"
        :title="successMsg ? 'Payment Success' : 'Complete Your Purchase'"
        size="md"
        @close="onClosePaymentModal"
      >
        <div class="modal-body">
          <div v-if="successMsg" class="success-container">
            <h3 class="text-center text-success">
              <i class="fa-regular fa-circle-check"></i> {{successMsg}}
            </h3>
          </div>

          <div v-else class="card">
            <div class="card-header payment-method-header">
              <div class="payment-method payment-method-logos stripe-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 24" role="img" width="38" height="24"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z" fill="#142688"/></svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 24" role="img" width="38" height="24"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><circle fill="#EB001B" cx="15" cy="12" r="7"/><circle fill="#F79E1B" cx="23" cy="12" r="7"/><path fill="#FF5F00" d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"/></svg>
                <small class="payment-method-read-more">and more...</small>
              </div>
            </div>
            <div class="card-body p-3 payment-method-block" id="card-stripe-form-modal">
              <div v-show="expressAvailable">
                <div id="express-checkout-element-modal"></div>
                <div class="express-divider"><span>or pay with card</span></div>
              </div>
              <div id="card-element-modal"></div>
              <div v-if="errorMessage" class="text-center text-danger mt-2">{{ errorMessage }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="buttons-footer-wrapper">
            <c-button
              theme="default"
              icon="fa-close"
              fill
              @click="onClosePaymentModal"
            >Close</c-button>

            <c-button
              v-if="!successMsg"
              theme="primary"
              icon="fa-arrow-right"
              :show-indicator="loading"
              :disabled="loading"
              fill
              uppercase
              @click="onHandleMakePayment()"
            ><span class="hide-small-mobile">Confirm & </span>Pay {{priceTitle}}</c-button>
          </div>
        </div>
      </c-modal>

      <c-modal
        v-if="showPurchasedAddonsModal"
        title="Add-ons History"
        size="md"
        @close="showPurchasedAddonsModal = false;"
      >
        <div class="modal-body">
          <div class="addons-history">
            <br />
            <table class="addon-table">
              <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Quantity</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="(item, index) in addonsHistory"
                :key="`addon-history-${index}`"
              >
                <td>{{ item.title }}</td>
                <td>${{ item.price }}</td>
                <td>{{ item.quantity }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <c-button
            theme="default"
            icon="fa-close"
            fill
            @click="showPurchasedAddonsModal = false;"
          >Close</c-button>
        </div>
      </c-modal>

      <c-modal
        v-if="selectedAddonForSessions"
        title="Details"
        size="md"
        @close="selectedAddonForSessions = null"
      >
        <div class="modal-body">
          <h4>Sessions for "{{selectedAddonForSessions.title}}":</h4>
          <br />
          <ul class="addon-modal-sessions-list">
            <li
              v-for="(session, index) in selectedAddonForSessions.sessions"
              :key="`modal-session-${index}`"
              class="addon-modal-session-item"
            >
              <router-link
                :to="{ name: 'Upgrade Ticket Session Detail', params: { sessionId: session.id }, query: { back: 'Upgrade Ticket' } }"
              >
                {{ session.title || `Session ${index + 1}` }}
              </router-link>
            </li>
          </ul>
        </div>
        <div class="modal-footer">
          <c-button
            theme="default"
            icon="fa-close"
            fill
            @click="selectedAddonForSessions = null"
          >Close</c-button>
        </div>
      </c-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { loadStripe } from "@stripe/stripe-js";
import { mountExpressCheckout } from "../../../global-declare-components/helpers/stripeExpressCheckout";
import common from "../../helpers/common";
import { attendeeSendData } from '../../api/index';

export default {
  mounted() {
    common.setHeaderText('Upgrade Ticket');
    if (!this.hasFetchedAddons) {
      this.$store.dispatch("ticketStore/fetchAddons", {id: this.attendeeId});
    }
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      windowWidth: window.innerWidth,
      showIndicator: false,
      showPaymentForm: false,
      showPurchasedAddonsModal: false,
      isPaymentModalOpen: false,
      isInitializingStripe: false,
      selectedAddonForSessions: null,
      stripe: null,
      hasInitializedStripe: false,
      elements: null,
      cardElement: null,
      expressCheckout: null,
      expressAvailable: false,
      loading: false,
      errorMessage: '',
      successMsg: '',
      paymentInfo: {
        client_secret: '',
        customer: { email: '', full_name: '' },
        invoice: { id: null, price: null },
        payment: { price: null },
        payment_required: false,
        stripe: { currency: 'USD', destination: '', public_key: '', stripe_account: '' },
      }
    }
  },
  computed: {
    ...mapGetters('ticketStore', ['addons', 'addonsHistory', 'isFetching', 'hasFetchedAddons']),
    ...mapGetters('attendeeStore', ['attendee']),
    totalPrice() { return this.paymentInfo?.payment?.price || 0; },
    priceTitle() {
      if (this.successMsg !== '') return '';
      return this.totalPrice ? `$${this.totalPrice}` : '';
    },
    payButtonLabel() {
      return this.paymentInfo?.payment_required ? `Pay ${this.priceTitle}` : 'Add to Ticket';
    },
    isMobile() { return this.windowWidth < 900; }
  },
  methods: {
    viewPurchasedAddons() {
      this.showPurchasedAddonsModal = true;
    },

    openSessionsModal(addon) {
      this.selectedAddonForSessions = addon;
    },

    onResize() {
      this.windowWidth = window.innerWidth;
    },
    onUpdateSelectedAddons() {
      const selectedCount = this.addons.reduce((sum, addon) => sum + parseInt(addon.selected || 0), 0);

      if (selectedCount === 0) {
        if (this.paymentInfo?.payment) this.paymentInfo.payment.price = 0;
        this.showPaymentForm = false;
        this.isInitializingStripe = false;
        // this.hasInitializedStripe = false;
        return;
      }

      const url = `/api/v2/attendees/${this.attendeeId}/addons/invoices/prepare`;
      this.showIndicator = true;
      attendeeSendData(url, { addons: this.addons })
        .then((res) => {
          this.paymentInfo = res.data?.data;
          this.successMsg = '';
          this.showPaymentForm = true;
          // this.hasInitializedStripe = false;

          if (!this.isMobile && this.paymentInfo?.payment_required) {
            this.isInitializingStripe = true;
            this.$nextTick(() => {
              this.initializeStripe();
            });
          }
        })
        .finally(() => { this.showIndicator = false; });
    },

    onOpenPaymentModal() {
      this.isPaymentModalOpen = true;
      this.isInitializingStripe = true;
      this.$nextTick(() => { this.initializeStripe(true); });
    },

    onClosePaymentModal() {
      this.isPaymentModalOpen = false;
      // this.successMsg = '';
      this.hasInitializedStripe = false;
    },

    async initializeStripe(isModal = false) {
      try {
        this.isInitializingStripe = true;
        let stripePromise;
        const publicKey = this.paymentInfo?.stripe?.public_key;

        // Gate on stripe_account alone: the PaymentIntent is created on the
        // connected account whenever one exists, so also requiring `destination`
        // (the external bank account) meant an organization without a payout
        // account initialised Stripe.js on the platform and got
        // "No such payment_intent". `destination` is not a valid loadStripe
        // option either, so it is gone.
        if (this.paymentInfo?.stripe?.stripe_account) {
          stripePromise = loadStripe(publicKey, {
            stripeAccount: this.paymentInfo?.stripe?.stripe_account
          });
        } else {
          stripePromise = loadStripe(publicKey);
        }

        this.stripe = await stripePromise;
        this.elements = this.stripe.elements({ clientSecret: this.paymentInfo?.client_secret, loader: 'always' });

        const elementId = isModal ? '#card-element-modal' : '#card-element';

        this.cardElement = this.elements.create('payment', {
          fields: { billingDetails: { name: 'never', email: 'never' } },
          wallets: { applePay: 'never', googlePay: 'never' }
        });
        this.cardElement.mount(elementId);

        this.mountWallets(isModal);

        this.hasInitializedStripe = true;
      } finally {
        this.isInitializingStripe = false;
      }
    },

    /**
     * Apple Pay / Google Pay buttons. The markup for these containers already
     * existed but nothing ever mounted into them.
     */
    mountWallets(isModal = false) {
      this.destroyWallets();

      this.expressCheckout = mountExpressCheckout({
        stripe: this.stripe,
        elements: this.elements,
        selector: isModal ? '#express-checkout-element-modal' : '#express-checkout-element',
        clientSecret: this.paymentInfo?.client_secret,
        returnUrl: this.paymentInfo?.redirect_url,
        onConfirm: () => this.confirmAddonPurchase(),
        onError: (message) => { this.errorMessage = message; },
        onAvailable: (available) => { this.expressAvailable = available; },
        onBusy: (busy) => { this.loading = busy; },
      });
    },

    destroyWallets() {
      this.expressAvailable = false;

      if (this.expressCheckout) {
        this.expressCheckout.destroy();
        this.expressCheckout = null;
      }
    },

    onMobilePayClick() {
      if (this.paymentInfo?.payment_required) {
        this.onOpenPaymentModal();
      } else {
        this.onHandleMakePayment();
      }
    },

    async onHandleMakePayment() {
      if (this.loading) return;

      this.errorMessage = '';

      // Free add-on: no Stripe payment needed, confirm directly.
      if (!this.paymentInfo?.payment_required) {
        this.loading = true;
        this.confirmAddonPurchase();
        return;
      }

      if (!this.stripe) return;

      this.loading = true;

      try {
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: this.paymentInfo.redirect_url,
            payment_method_data: {
              billing_details: {
                name: `${this.paymentInfo?.customer?.full_name}`,
                email: this.paymentInfo?.customer?.email
              }
            }
          },
          redirect: 'if_required'
        });

        if (error) {
          this.errorMessage = error.message;
          this.loading = false;
          return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          this.confirmAddonPurchase();
        } else {
          this.loading = false;
        }
      } catch (e) {
        this.errorMessage = "An unexpected error occurred.";
        this.loading = false;
      }
    },

    confirmAddonPurchase() {
      const url = `/api/v2/attendees/${this.attendeeId}/addons/invoices/${this.paymentInfo.invoice.id}/confirm`;
      const paymentRequired = this.paymentInfo?.payment_required;

      // Returned so callers (the wallet buttons) can await the confirmation
      // and keep their busy state in step with it.
      return attendeeSendData(url).then(() => {
        this.successMsg = paymentRequired
          ? 'The payment has been processed successfully.'
          : 'The add-on has been added to your ticket successfully.';
        this.showPaymentForm = false;
        // Keep the mobile modal open so the success message remains visible.
        this.paymentInfo.payment.price = 0;
        this.addons.forEach(addon => { addon.selected = 0; });
        this.$store.dispatch("ticketStore/fetchAddons", {id: this.attendeeId});
      }).catch(() => {
        this.errorMessage = "An unexpected error occurred while confirming your purchase.";
      }).finally(() => {
        this.loading = false;
        this.$store.commit("agendaStore/updateHasFetchedData", false);
        this.$store.dispatch("agendaStore/fetchAgenda", {attendeeId: this.attendeeId, conferenceId: this.attendee?.conference_id});
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.express-divider {
  display: flex;
  align-items: center;
  margin: 18px 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  span { padding: 0 10px; }
}

.upgrade-ticket-wrapper {
  background-color: white;
  border-radius: var(--attendee-card-border-radius);
  padding: var(--attendee-gap-md);
}

.sticky-mobile-header {
  @media (max-width: 899px) {
    position: sticky;
    top: 50px;
    z-index: 100;
    background: white;
    margin: calc(var(--attendee-gap-md) * -1) calc(var(--attendee-gap-md) * -1) 20px;
    padding: var(--attendee-gap-md);
    border-bottom: 1px solid #eee;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  }
}

.mobile-only-pay {
  margin-top: 5px;
  width: 100%;
  ::v-deep button { width: 100%; justify-content: center; }
}

.addon-table {
  width: 100%;
  border-collapse: collapse;
  //margin-bottom: 20px;
  background-color: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  thead {
    background-color: #f5f7fa;
    text-align: left;
    th { padding: 12px 16px; font-size: 14px; font-weight: 600; color: #2c3e50; }
  }
  tbody tr {
    border-top: 1px solid #e6ecf1;
    &:hover { background-color: #f9fbfd; }
    td { padding: 12px 16px; font-size: 14px; color: #444; }
  }
}

.payment-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
}

.payment-block {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  flex: 1 1 48%;
}

.full-width-block { flex: 1 1 100% !important; }

.addon-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 15px;
}

.addon-info-wrapper { flex: 1; }
.addon-title { font-size: 18px; font-weight: 600; }
.addon-price { font-size: 17px; margin: 5px 0; }
.addon-quantity-wrapper { width: 100px; }

.addon_sessions__item {
  color: var(--attendee-brand-color);
  text-decoration: underline;
  //font-weight: 600;
  cursor: pointer;
  list-style: none;
}

.addon-modal-sessions-list {
  padding: 0;
  margin: 0;
  list-style: none;
}

.addon-modal-session-item {
  padding: 5px 0;
  //border-bottom: 1px solid #eee;
  //&:last-child { border-bottom: none; }
  a {
    color: var(--attendee-brand-color);
    font-weight: 600;
    font-size: 16px;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}

.stripe-wrapper { display: flex; align-items: center; justify-content: center; gap: 10px; }
.payment-method-block { min-height: 240px; }
.purchase-button-wrapper { margin-top: 20px; display: flex; justify-content: flex-end; }

//.footer-flex {
//  display: flex !important;
//  justify-content: space-between !important;
//  align-items: center !important;
//}

.buttons-footer-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.success-container {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 600px) {
  .no-shadow-mobile { box-shadow: none; padding: 0; }
  .addon-wrapper { flex-direction: column; }
  .addon-quantity-wrapper { width: 100%; margin-top: 10px; }
}

@media (max-width: 375px) {
  .hide-small-mobile {
    display: none;
  }
}
</style>
